import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import HeroSection from './components/HeroSection';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import LoadingOverlay from './components/ui/LoadingOverlay';
import AppErrorBoundary from './components/AppErrorBoundary';
import SeoHead from './components/SeoHead';
import DirectoryBadges from './components/DirectoryBadges';
import AppDocumentTitle from './components/AppDocumentTitle';
import CookieConsentBanner from './components/CookieConsentBanner';

// Lazy load below-the-fold homepage components
const HomeGettingStartedSection = lazy(
  () => import('./components/HomeGettingStartedSection')
);
const HomeUseCasesSection = lazy(
  () => import('./components/HomeUseCasesSection')
);
const FeaturesSection = lazy(() => import('./components/FeaturesSection'));
const TestimonialsSection = lazy(
  () => import('./components/TestimonialsSection')
);
const CTASection = lazy(() => import('./components/CTASection'));
const Footer = lazy(() => import('./components/Footer'));

// Lazy load route components for code splitting - only load what's needed per page
const CreatePage = lazy(() => import('./components/CreatePage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const DpaPage = lazy(() => import('./components/DpaPage'));
const LimitationsPage = lazy(() => import('./components/LimitationsPage'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const AppLayout = lazy(() => import('./components/layouts/AppLayout'));
const DashboardPage = lazy(() => import('./components/app/DashboardPage'));
const ApiKeysPage = lazy(() => import('./components/app/ApiKeysPage'));
const BillingPage = lazy(() => import('./components/app/BillingPage'));
const IntegrationsPage = lazy(
  () => import('./components/app/IntegrationsPage')
);
const ProfilePage = lazy(() => import('./components/app/ProfilePage'));
const OrganizationPage = lazy(
  () => import('./components/app/OrganizationPage')
);
const InviteAcceptPage = lazy(
  () => import('./components/app/InviteAcceptPage')
);
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
const OAuthConsentPage = lazy(() => import('./components/OAuthConsentPage'));
const McpDocsPage = lazy(() => import('./components/docs/McpDocsPage'));
const DocsPage = lazy(() => import('./components/docs/DocsPage'));
const NotFoundPage = lazy(() => import('./components/NotFoundPage'));
const ServerErrorPage = lazy(() => import('./components/ServerErrorPage'));
const ForbiddenPage = lazy(() => import('./components/ForbiddenPage'));
const UnauthorizedPage = lazy(() => import('./components/UnauthorizedPage'));
const BadRequestPage = lazy(() => import('./components/BadRequestPage'));
const BlogRouter = lazy(() => import('./components/blog/BlogRouter'));
const OgCardPreviewPage = lazy(
  () => import('./components/tools/OgCardPreviewPage')
);
const OgImageSizesPage = lazy(
  () => import('./components/tools/OgImageSizesPage')
);
const BulkCreateCsvCheckerPage = lazy(
  () => import('./components/tools/BulkCreateCsvCheckerPage')
);
const WhatsNewRouter = lazy(() =>
  import('./components/blog/BlogRouter').then(module => ({
    default: module.WhatsNewRouter,
  }))
);
const IntegrationsRouter = lazy(() =>
  import('./components/blog/BlogRouter').then(module => ({
    default: module.IntegrationsRouter,
  }))
);
const UseCaseRouter = lazy(() =>
  import('./components/blog/BlogRouter').then(module => ({
    default: module.UseCaseRouter,
  }))
);
const CompareRouter = lazy(() => import('./components/compare/CompareRouter'));
const AlternativesRouter = lazy(
  () => import('./components/alternatives/AlternativesRouter')
);
const TutorialsRouter = lazy(
  () => import('./components/tutorials/TutorialsRouter')
);
const FaqRouter = lazy(() => import('./components/faq/FaqRouter'));
const GlossaryRouter = lazy(
  () => import('./components/glossary/GlossaryRouter')
);
const ToolsRouter = lazy(() => import('./components/tools/ToolsRouter'));

function HomePage() {
  return (
    <>
      <SeoHead />
      <DirectoryBadges />
      <div className='min-h-screen bg-white'>
        <HeroSection />
        <Suspense fallback={null}>
          <HomeGettingStartedSection />
          <TestimonialsSection />
          <HomeUseCasesSection />
          <FeaturesSection />
          <CTASection />
          <Footer />
        </Suspense>
      </div>
    </>
  );
}

// Loading fallback component for lazy-loaded routes
const RouteLoadingFallback = () => (
  <>
    <SeoHead />
    <LoadingOverlay
      isVisible={true}
      altText='Loading'
      messages={['Loading page...']}
      fullScreen={true}
    />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppErrorBoundary>
          <Router>
            <AppDocumentTitle />
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
                <Route path='/' element={<HomePage />} />
                <Route path='/docs' element={<DocsPage />} />
                <Route path='/docs/mcp' element={<McpDocsPage />} />
                <Route path='/pricing' element={<PricingPage />} />
                <Route path='/terms' element={<TermsPage />} />
                <Route path='/privacy' element={<PrivacyPage />} />
                <Route path='/dpa' element={<DpaPage />} />
                <Route path='/limitations' element={<LimitationsPage />} />
                <Route path='/reset-password' element={<ResetPasswordPage />} />
                <Route path='/oauth/consent' element={<OAuthConsentPage />} />
                <Route path='/invite/:token' element={<InviteAcceptPage />} />

                {/* Error pages */}
                <Route path='/400' element={<BadRequestPage />} />
                <Route path='/400.html' element={<BadRequestPage />} />
                <Route path='/401' element={<UnauthorizedPage />} />
                <Route path='/401.html' element={<UnauthorizedPage />} />
                <Route path='/403' element={<ForbiddenPage />} />
                <Route path='/403.html' element={<ForbiddenPage />} />
                <Route path='/404' element={<NotFoundPage />} />
                <Route path='/404.html' element={<NotFoundPage />} />
                <Route path='/500' element={<ServerErrorPage />} />
                <Route path='/500.html' element={<ServerErrorPage />} />

                {/* Blog routes */}
                <Route path='/blog/*' element={<BlogRouter />} />
                <Route
                  path='/resources/og-templates'
                  element={<Navigate to='/blog/og-template-sizes/' replace />}
                />
                <Route path='/whats-new/*' element={<WhatsNewRouter />} />
                <Route
                  path='/integrations/*'
                  element={<IntegrationsRouter />}
                />
                <Route path='/use-cases/*' element={<UseCaseRouter />} />
                <Route path='/tutorials/*' element={<TutorialsRouter />} />

                {/* pSEO routes */}
                <Route path='/compare/*' element={<CompareRouter />} />
                <Route
                  path='/alternatives/*'
                  element={<AlternativesRouter />}
                />
                <Route path='/faq/*' element={<FaqRouter />} />
                <Route path='/glossary/*' element={<GlossaryRouter />} />

                {/* Free tools (top-level paths) */}
                <Route path='/canva-palette/*' element={<ToolsRouter />} />
                <Route
                  path='/og-card-preview'
                  element={<OgCardPreviewPage />}
                />
                <Route path='/og-image-sizes' element={<OgImageSizesPage />} />
                <Route
                  path='/bulk-create-csv-checker'
                  element={<BulkCreateCsvCheckerPage />}
                />

                {/* App routes with sidebar layout */}
                <Route
                  path='/app/*'
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path='dashboard' element={<DashboardPage />} />
                  <Route path='api-keys' element={<ApiKeysPage />} />
                  <Route path='billing' element={<BillingPage />} />
                  <Route path='integrations' element={<IntegrationsPage />} />
                  <Route path='create' element={<CreatePage />} />
                  <Route path='profile' element={<ProfilePage />} />
                  <Route path='org' element={<OrganizationPage />} />
                </Route>

                <Route path='*' element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <CookieConsentBanner />
          </Router>
        </AppErrorBoundary>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
